import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import axios from "axios";
import { ShieldAlert, RefreshCw, ExternalLink, Check, X, LogOut as Out } from "lucide-react";

const ADMIN_KEY_STORAGE = "crash_admin_pwd";

// Admin password lives in sessionStorage (cleared when tab closes) — never localStorage.
const getStoredAdminPwd = () => {
  try { return sessionStorage.getItem(ADMIN_KEY_STORAGE) || ""; }
  catch { return ""; }
};
const setStoredAdminPwd = (v) => {
  try { sessionStorage.setItem(ADMIN_KEY_STORAGE, v); } catch { /* storage disabled */ }
};
const clearStoredAdminPwd = () => {
  try { sessionStorage.removeItem(ADMIN_KEY_STORAGE); } catch { /* storage disabled */ }
};

function makeAdmin(pwd) {
  return axios.create({
    baseURL: API,
    headers: { "X-Admin-Password": pwd, "Content-Type": "application/json" },
  });
}

export default function Admin() {
  const [pwd, setPwd] = useState(getStoredAdminPwd());
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);

  const tryLogin = useCallback(async (p, silent = false) => {
    setLoading(true);
    try {
      const a = makeAdmin(p);
      const [s, w] = await Promise.all([a.get("/admin/stats"), a.get("/admin/withdrawals")]);
      setStats(s.data);
      setPending(w.data);
      setAuthed(true);
      setStoredAdminPwd(p);
      if (!silent) toast.success("Admin authenticated");
    } catch (e) {
      console.error("Admin auth failed:", e?.response?.status, e?.message);
      setAuthed(false);
      if (!silent) toast.error("Invalid admin password");
      clearStoredAdminPwd();
    } finally {
      setLoading(false);
    }
  }, []);

  // Try cached pwd on mount
  useEffect(() => {
    const cached = getStoredAdminPwd();
    if (cached) tryLogin(cached, true);
  }, [tryLogin]);

  const refresh = () => tryLogin(pwd, true);

  const logout = () => {
    clearStoredAdminPwd();
    setAuthed(false);
    setPwd("");
    setStats(null);
    setPending([]);
  };

  const complete = async (payout_id) => {
    const tx_hash = window.prompt(`Paste the on-chain tx hash you used to send USDT for ${payout_id}:`)?.trim();
    if (!tx_hash) return;
    if (!/^0x[0-9a-fA-F]{64}$/.test(tx_hash)) {
      toast.error("tx hash must be 0x + 64 hex chars");
      return;
    }
    try {
      await makeAdmin(pwd).post(`/admin/withdrawals/${payout_id}/complete`, { tx_hash });
      toast.success(`Marked ${payout_id} FINISHED`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Complete failed");
    }
  };

  const cancel = async (payout_id, userName) => {
    if (!window.confirm(`Cancel withdrawal ${payout_id} for ${userName}? Funds will be refunded to their balance.`)) return;
    try {
      await makeAdmin(pwd).post(`/admin/withdrawals/${payout_id}/cancel`);
      toast.success(`Cancelled ${payout_id} · refunded`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-[calc(100vh-4rem)] grid place-items-center p-6" data-testid="admin-login">
        <div className="panel p-8 w-full max-w-md">
          <div className="flex items-center gap-2 mb-4">
            <ShieldAlert className="w-5 h-5 text-neon-red" />
            <div className="label-mono text-neon-red">// admin · restricted area</div>
          </div>
          <h1 className="font-display font-black text-3xl tracking-tighter mb-2">ADMIN ACCESS</h1>
          <p className="text-white/50 text-sm font-body mb-6">Process pending withdrawals and view platform stats. Requires the X-Admin-Password from the backend env.</p>
          <form onSubmit={(e) => { e.preventDefault(); tryLogin(pwd); }}>
            <label className="label-mono">Admin Password</label>
            <input
              type="password"
              className="field mt-2"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="••••••••"
              autoFocus
              data-testid="admin-password-input"
            />
            <button type="submit" disabled={!pwd || loading} className="btn-neon w-full mt-4 disabled:opacity-30" data-testid="admin-login-button">
              {loading ? "AUTHENTICATING…" : "Enter Admin Console →"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-10" data-testid="admin-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="label-mono mb-2">// admin · console</div>
          <h1 className="font-display font-black text-3xl lg:text-5xl tracking-tighter">OPERATIONS.</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={refresh} className="btn-ghost text-xs" disabled={loading} data-testid="admin-refresh"><RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /></button>
          <button onClick={logout} className="btn-ghost text-xs" data-testid="admin-logout"><Out className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard label="Pending" value={stats.withdrawals_pending} sub={`${stats.pending_total_usdt} USDT`} color="text-neon-red" testid="stat-pending" />
          <StatCard label="Completed" value={stats.withdrawals_finished} color="text-neon-green" testid="stat-finished" />
          <StatCard label="Cancelled" value={stats.withdrawals_cancelled} color="text-white/60" testid="stat-cancelled" />
          <StatCard label="Users" value={stats.users} color="text-neon-cyan" testid="stat-users" />
          <StatCard label="Deposits" value={stats.deposits_finished} color="text-neon-cyan" testid="stat-deposits" />
        </div>
      )}

      <div className="panel p-0 overflow-hidden" data-testid="pending-withdrawals-table">
        <div className="px-6 py-4 flex items-center justify-between border-b border-neon-line">
          <h2 className="font-display font-black text-xl tracking-tight">PENDING WITHDRAWALS</h2>
          <div className="label-mono">{pending.length} queued</div>
        </div>
        {pending.length === 0 ? (
          <div className="p-10 text-center font-mono text-white/40">no pending withdrawals</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neon-line">
                  <th className="text-left p-4 label-mono">User</th>
                  <th className="text-left p-4 label-mono">Address</th>
                  <th className="text-right p-4 label-mono">Amount</th>
                  <th className="text-right p-4 label-mono">Fee</th>
                  <th className="text-right p-4 label-mono">Send (net)</th>
                  <th className="text-left p-4 label-mono">Created</th>
                  <th className="text-right p-4 label-mono">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((w) => (
                  <tr key={w.payout_id} className="border-b border-neon-line/40 hover:bg-neon-green/5 transition-colors" data-testid={`pending-row-${w.payout_id}`}>
                    <td className="p-4 font-mono text-xs">
                      <div className="text-white">{w.user?.name || "?"}</div>
                      <div className="text-white/40 text-[10px]">{w.user?.email}</div>
                    </td>
                    <td className="p-4 font-mono text-xs">
                      <a href={`https://etherscan.io/address/${w.address}`} target="_blank" rel="noreferrer" className="text-neon-cyan hover:underline inline-flex items-center gap-1">
                        {w.address.slice(0, 8)}…{w.address.slice(-6)}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                    <td className="p-4 font-mono text-right text-white">{w.amount.toFixed(2)}</td>
                    <td className="p-4 font-mono text-right text-white/40">{(w.fee ?? 0).toFixed(2)}</td>
                    <td className="p-4 font-mono text-right text-neon-green font-bold" data-testid={`pending-net-${w.payout_id}`}>{(w.net_amount ?? w.amount).toFixed(2)}</td>
                    <td className="p-4 font-mono text-[10px] text-white/40">{new Date(w.created_at).toLocaleString()}</td>
                    <td className="p-4">
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => complete(w.payout_id)} className="btn-neon !py-1.5 !px-3 text-xs flex items-center gap-1" data-testid={`complete-${w.payout_id}`}>
                          <Check className="w-3 h-3" /> Complete
                        </button>
                        <button onClick={() => cancel(w.payout_id, w.user?.name || "user")} className="btn-danger !py-1.5 !px-3 text-xs flex items-center gap-1" data-testid={`cancel-${w.payout_id}`}>
                          <X className="w-3 h-3" /> Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-6 panel p-4 text-xs font-mono text-white/50" data-testid="admin-howto">
        <div className="label-mono text-neon-green mb-2">// how to complete a withdrawal</div>
        <ol className="space-y-1 list-decimal list-inside">
          <li>Send the <span className="text-neon-green">net amount</span> (in USDT/ERC20) from the platform wallet to the user&apos;s address using any wallet (MetaMask, hardware wallet, etc.).</li>
          <li>Copy the transaction hash from your wallet / Etherscan.</li>
          <li>Click <b>Complete</b> and paste the tx hash — the record will be marked FINISHED.</li>
          <li>If you need to reject, click <b>Cancel</b> — the full requested amount (incl. fee) is refunded to the user&apos;s balance.</li>
        </ol>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, color, testid }) {
  return (
    <div className="panel p-4" data-testid={testid}>
      <div className="label-mono">{label}</div>
      <div className={`font-mono font-black text-2xl mt-1 ${color}`}>{value}</div>
      {sub && <div className="font-mono text-[10px] text-white/40 mt-1">{sub}</div>}
    </div>
  );
}
